import React, {
  useState,
  useEffect,
  useRef,
  ClipboardEvent,
  KeyboardEvent,
} from 'react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import LoaderOverlay from '../common/LoaderOverlay';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { ShieldCheck } from 'lucide-react';

const ModalOverlay = ({ children }: { children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    {children}
  </div>
);

const ModalContent = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={className} onClick={e => e.stopPropagation()}>
    {children}
  </div>
);

interface VerificationModalProps {
  isOpen: boolean;
  type: 'email' | 'phone';
  value: string;
  onSuccess: (nextStep: 'email' | 'phone' | 'dashboard') => void;
  onClose?: () => void;
  otpAlreadySent?: boolean;
}

const VerificationModal = ({
  isOpen,
  type,
  value,
  onSuccess,
  otpAlreadySent = false,
}: VerificationModalProps) => {
  const { user, updateUser } = useAuth();
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current && isOpen) {
      setOtp(Array(6).fill(''));
      setTimer(otpAlreadySent ? 60 : 0);
      setError(null);
      isInitialMount.current = false;
    }
    if (!isOpen) {
      isInitialMount.current = true;
    }
  }, [isOpen, otpAlreadySent]);

  useEffect(() => {
    let interval: any;
    if (isOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOpen, timer]);

  const handleResendOTP = async () => {
    setResending(true);
    try {
      let response;
      if (type === 'email') {
        response = await authAPI.sendEmailOTP(value);
      } else {
        response = await authAPI.sendPhoneOTP(value);
      }
      const message =
        response.message || response.toastMessage || 'OTP resent successfully';
      toast.success(message);
      setTimer(60);
      setOtp(Array(6).fill(''));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to resend OTP';
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    if (digit || value === '') {
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);
      if (error) setError(null);

      if (digit && index < 5) {
        const nextInput = document.getElementById(
          `otp-${index + 1}`
        ) as HTMLInputElement;
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(
        `otp-${index - 1}`
      ) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
    if (e.key === 'Enter') {
      const otpString = otp.join('');
      if (otpString.length === 6) {
        handleVerify(e as unknown as React.FormEvent);
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').slice(0, 6);
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
      const focusIndex = Math.min(newOtp.length, 5);
      const input = document.getElementById(
        `otp-${focusIndex}`
      ) as HTMLInputElement;
      if (input) input.focus();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length < 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (type === 'email') {
        response = await authAPI.verifyEmailOTP(value, Number(otpString));
      } else {
        response = await authAPI.verifyPhoneOTP(value, Number(otpString));
      }

      if (response.success) {
        toast.success(
          response.message ||
            `${type === 'email' ? 'Email' : 'Phone'} verified successfully`
        );
        setError(null);

        if (user?.id) {
          try {
            const freshUser = await authAPI.getProfile(user.id);
            updateUser(freshUser);
          } catch (pError) {
            console.error('Profile refresh failed:', pError);
            updateUser({
              emailVerified: response.verification?.emailVerified,
              phoneVerified: response.verification?.phoneVerified,
            });
          }
        }

        onSuccess(response.verification?.nextStep || 'dashboard');
      } else {
        const errorMsg =
          response.message || response.error || 'Verification failed';
        setError(errorMsg);
        toast.error(errorMsg);

        if (response.canResendOtp) {
          setTimer(0);
        }
      }
    } catch (err: any) {
      const apiError = err?.response?.data;
      const errorMsg =
        apiError?.error ||
        apiError?.message ||
        err.message ||
        'Verification failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <LoaderOverlay
      isLoading={loading || resending}
      loadingText={
        resending ? 'Sending...' : loading ? 'Verifying...' : 'Loading...'
      }
    >
      <ModalOverlay>
        <ModalContent>
          <Card className="max-w-md w-full shadow-2xl border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-xl font-bold">
                  Verify {type === 'email' ? 'Email' : 'Phone'}
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              <form onSubmit={handleVerify} className="space-y-6">
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <CardDescription className="text-base">
                      Enter the 6-digit verification code sent to your{' '}
                      {type === 'email' ? 'email' : 'phone'}
                    </CardDescription>
                  </div>

                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        placeholder="0"
                        onChange={e => handleInputChange(index, e.target.value)}
                        onKeyDown={e => handleKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        className={`w-12 h-14 text-center text-2xl font-semibold rounded-lg focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 ${error ? 'border-red-500 focus:border-red-500' : ''}`}
                        disabled={loading}
                      />
                    ))}
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 text-center">{error}</p>
                  )}

                  <div className="flex justify-end text-sm">
                    {timer > 0 ? (
                      <p className="text-muted-foreground">
                        Resend code in{' '}
                        <span className="font-semibold tabular-nums">
                          {timer}s
                        </span>
                      </p>
                    ) : (
                      <Button
                        type="button"
                        variant="link"
                        onClick={handleResendOTP}
                        disabled={resending}
                        className="h-auto p-0 font-semibold"
                      >
                        {resending ? 'Resending...' : 'Resend code'}
                      </Button>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || otp.join('').length !== 6}
                  size="lg"
                  className="w-full"
                >
                  Verify & Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        </ModalContent>
      </ModalOverlay>
    </LoaderOverlay>
  );
};

export default VerificationModal;
